/**
 * Guess Who AI - Probability-based elimination strategy
 * Asks questions that eliminate ~50% of remaining characters
 */

import type { Character } from '../../types/game'

export class GuessWhoAI {
  private remainingCharacters: Character[]
  private allCharacters: Character[]
  private askedQuestions: Set<string>

  constructor(characters: Character[]) {
    this.allCharacters = characters
    this.remainingCharacters = [...characters]
    this.askedQuestions = new Set()
  }

  /**
   * Find the best attribute to ask about (closest to 50/50 split)
   */
  private findBestAttribute(): { attribute: string; value: any; expectedElimination: number } {
    const attributes = ['gender', 'hairColor', 'hasGlasses', 'hasHat', 'facialHair', 'hairStyle']
    let bestAttribute = { attribute: '', value: null, expectedElimination: 0 }
    let bestSplit = 1 // Lower is better (closer to 0.5 = 50/50 split)

    for (const attr of attributes) {
      const values = new Set(this.remainingCharacters.map(c => (c.attributes as any)[attr]))

      for (const value of values) {
        const matchCount = this.remainingCharacters.filter(
          c => (c.attributes as any)[attr] === value
        ).length
        const split = Math.abs(matchCount / this.remainingCharacters.length - 0.5)
        const questionKey = `${attr}:${value}`

        // Skip if already asked
        if (this.askedQuestions.has(questionKey)) continue

        if (split < bestSplit) {
          bestSplit = split
          bestAttribute = {
            attribute: attr,
            value,
            expectedElimination: Math.min(matchCount, this.remainingCharacters.length - matchCount),
          }
        }
      }
    }

    return bestAttribute
  }

  /**
   * Generate a natural language question
   */
  askQuestion(): string {
    if (this.remainingCharacters.length <= 2) {
      // Make a guess
      return `Is your character ${this.remainingCharacters[0].name}?`
    }

    const best = this.findBestAttribute()

    if (!best.attribute) {
      // Fallback: guess randomly
      const randomChar = this.remainingCharacters[
        Math.floor(Math.random() * this.remainingCharacters.length)
      ]
      return `Is your character ${randomChar.name}?`
    }

    this.askedQuestions.add(`${best.attribute}:${best.value}`)

    // Convert attribute to natural question
    const questions: Record<string, (val: any) => string> = {
      gender: (val) => `Is your character ${val === 'male' ? 'male' : 'female'}?`,
      hairColor: (val) => `Does your character have ${val} hair?`,
      hasGlasses: (val) => `Does your character ${val ? 'wear glasses' : 'not wear glasses'}?`,
      hasHat: (val) => `Does your character ${val ? 'wear a hat' : 'not wear a hat'}?`,
      facialHair: (val) => `Does your character have ${val ? 'facial hair' : 'no facial hair'}?`,
      hairStyle: (val) => `Does your character have ${val} hair?`,
    }

    return questions[best.attribute](best.value)
  }

  /**
   * Process answer and eliminate characters
   */
  processAnswer(question: string, answer: boolean): void {
    if (question.includes('Is your character')) {
      // It was a guess - if wrong, eliminate that character
      const characterName = question.match(/Is your character (.+)\?/)?.[1]
      if (characterName && !answer) {
        this.remainingCharacters = this.remainingCharacters.filter(c => c.name !== characterName)
      }
      return
    }

    // Parse the question to determine what attribute was asked
    const attributeMatches = [
      { pattern: /male|female/, attr: 'gender', getValue: () => question.includes('male') ? 'male' : 'female' },
      { pattern: /(\w+) hair/, attr: 'hairColor', getValue: () => question.match(/(\w+) hair/)?.[1] },
      { pattern: /glasses/, attr: 'hasGlasses', getValue: () => true },
      { pattern: /hat/, attr: 'hasHat', getValue: () => true },
      { pattern: /facial hair/, attr: 'facialHair', getValue: () => true },
    ]

    for (const match of attributeMatches) {
      if (match.pattern.test(question)) {
        const value = match.getValue()
        this.remainingCharacters = this.remainingCharacters.filter(c => {
          const charValue = (c.attributes as any)[match.attr]
          return answer ? charValue === value : charValue !== value
        })
        break
      }
    }
  }

  /**
   * Make a final guess when confident
   */
  makeGuess(): Character | null {
    if (this.remainingCharacters.length === 1) {
      return this.remainingCharacters[0]
    }

    if (this.remainingCharacters.length === 0) {
      // Shouldn't happen, but fallback to random
      return this.allCharacters[Math.floor(Math.random() * this.allCharacters.length)]
    }

    return null // Not ready to guess yet
  }

  /**
   * Eliminate a character (when opponent asks and gets answer)
   */
  eliminateCharacter(characterId: number, shouldEliminate: boolean): void {
    if (shouldEliminate) {
      this.remainingCharacters = this.remainingCharacters.filter(c => c.id !== characterId)
    }
  }

  /**
   * Get remaining character count
   */
  getRemainingCount(): number {
    return this.remainingCharacters.length
  }

  /**
   * Reset AI state for new game
   */
  reset(characters: Character[]): void {
    this.allCharacters = characters
    this.remainingCharacters = [...characters]
    this.askedQuestions.clear()
  }
}
